/**
 * Checkout Success Page
 * Shown after the card confirm. The billing subsystem activates the subscription asynchronously,
 * so the page waits for the read to report a plan that differs from the one before the confirm
 * (CheckoutPage passes that read) before it claims success (BR-86).
 */

import { useEffect, useMemo, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import type { SubscriptionStatus } from '../api/billing';
import { awaitActivation, useBillingSubscription } from '../hooks/useBilling';
import { Button } from '../components/ui/button';

function SuccessCard({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <div className="max-w-4xl mx-auto pb-6">
      <div className="px-[var(--page-padding-x)]">
        <div className="max-w-lg mx-auto pt-10">
          <div className="bg-card rounded-lg border border-border p-6 text-center" data-testid={testId}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export function CheckoutSuccessPage() {
  const navigate = useNavigate();
  // CheckoutPage passes the subscription read the card confirm started from; activation is a plan that differs from it.
  const { state } = useLocation();
  const before = (state as { before?: SubscriptionStatus } | null)?.before;
  const wait = useMemo(() => awaitActivation(before), [before]);
  // The hook bounds the wait: after the timeout it stops polling and `timedOut` stays set, so this
  // mount never claims success, even if a later read reports an active plan.
  const { data: subscription, outcomeReached, timedOut } = useBillingSubscription(wait);
  const activated = !timedOut && subscription !== undefined && outcomeReached;

  useEffect(() => {
    if (!activated) return undefined;
    // Auto-redirect to the billing page 5 seconds after activation is confirmed.
    const timer = setTimeout(() => navigate('/settings/billing'), 5000);
    return () => clearTimeout(timer);
  }, [activated, navigate]);

  const goToBilling = (
    <Button asChild variant={activated ? 'default' : 'outline'} className="min-h-[44px]">
      <Link to="/settings/billing" data-testid="checkout-success-billing-link">
        Go to Billing
      </Link>
    </Button>
  );

  if (timedOut) {
    return (
      <SuccessCard testId="checkout-success-unconfirmed">
        <h1 className="text-2xl font-semibold text-foreground m-0 mb-4">Payment not confirmed</h1>
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm mb-5">
          <p className="text-destructive font-semibold m-0 mb-1">We couldn&apos;t confirm your payment</p>
          <p className="text-foreground m-0">
            Your subscription hasn&apos;t been activated yet. Check your billing page for its current status.
          </p>
        </div>
        {goToBilling}
      </SuccessCard>
    );
  }

  if (!activated) {
    return (
      <SuccessCard testId="checkout-success-progress">
        <div role="status" className="flex flex-col items-center">
          <Loader2 className="w-10 h-10 mb-5 animate-spin text-primary" aria-hidden="true" />
          <h1 className="text-2xl font-semibold text-foreground m-0 mb-3">Confirming your payment...</h1>
          <p className="text-base text-muted-foreground mb-5">This usually takes a few seconds.</p>
        </div>
        {goToBilling}
      </SuccessCard>
    );
  }

  return (
    <SuccessCard testId="checkout-success-activated">
      {/* Same element type and position as the progress state's live region, so the swap is announced. */}
      <div role="status">
        <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-5">
          <span className="text-5xl text-success" aria-hidden="true">✓</span>
        </div>

        <h1 className="text-2xl font-semibold text-foreground m-0 mb-3">
          Subscription Activated!
        </h1>

        <p className="text-base text-foreground mb-5">
          Your subscription to {subscription.bundleName || 'the selected plan'} has been activated successfully.
        </p>
      </div>

      <p className="text-xs text-muted-foreground mb-5">
        Redirecting to billing page in 5 seconds...
      </p>

      {goToBilling}
    </SuccessCard>
  );
}

export default CheckoutSuccessPage;
